import LogoFerinaCura from "@/assets/logos/Header.svg";
import { useTheme } from "@/theme/ThemeProvider";
import { getScreenGradientColors } from "@/theme/themes";
import { AppButton } from "@/ui/components/AppButton";
import { Card } from "@/ui/components/Card";
import { HIT_TARGET_MIN } from "@/ui/components/listRowA11y";
import { FinderDraftFields } from "@/ui/finder/FinderDraftFields";
import { useFinderMode } from "@/ui/finder/FinderModeContext";
import {
  FINDER_CONTINUE,
  FINDER_DISCARD_LABEL,
  FINDER_DROP_CONFIRM,
  FINDER_DROP_DIALOG_BODY,
  FINDER_DROP_DIALOG_TITLE,
  FINDER_FINISH_LABEL,
  FINDER_GATE_INTRO,
  FINDER_GATE_TITLE,
  FINDER_HANDOVER_ACTION,
  FINDER_HANDOVER_BODY,
  FINDER_HANDOVER_TITLE,
  FINDER_KEEP_CONFIRM,
  FINDER_KEEP_DIALOG_BODY,
  FINDER_KEEP_DIALOG_TITLE,
  FINDER_NAME_HINT,
} from "@/ui/finder/finderMode";
import { FinderPrivacyAck } from "@/ui/finder/FinderPrivacyAck";
import { FinderPrivacyNotice } from "@/ui/finder/FinderPrivacyNotice";
import { finderHost } from "@/ui/finder/finderHost";
import { bindWebOverlayTrap, webTitleProps } from "@/ui/finder/finderOverlayFocus";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const actionStyle = { alignSelf: "stretch" as const, minHeight: HIT_TARGET_MIN };

function focusNode(node: { focus?: () => void } | null) {
  if (Platform.OS === "web") {
    node?.focus?.();
    return;
  }
  if (!node) return;
  const handle = finderHost.findNodeHandle(node);
  if (handle) finderHost.setAccessibilityFocus(handle);
}

/**
 * Vollbild-Gate: Finder sieht Kontaktdaten und Datenschutz.
 * Die App bleibt zu, bis die Pflegekraft den Übergabe-Screen bestätigt.
 */
export function FinderModeGate() {
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const {
    storageReady,
    active,
    phase,
    draft,
    nameHint,
    updateDraft,
    requestFinish,
    requestDiscard,
    confirmKeep,
    confirmDiscard,
    cancelDialog,
    hardwareBack,
    completeHandover,
  } = useFinderMode();
  const titleRef = useRef<Text>(null);
  const handoverTitleRef = useRef<Text>(null);
  const dialogTitleRef = useRef<Text>(null);
  const nameRef = useRef<TextInput>(null);
  const gateRef = useRef<View>(null);
  const [renderError, setRenderError] = useState<Error | null>(null);

  useEffect(() => {
    if (!active || Platform.OS === "web") return;
    try {
      const sub = finderHost.addBackHandler(() => {
        hardwareBack();
        return true;
      });
      return () => sub.remove();
    } catch (error) {
      setRenderError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [active, hardwareBack]);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    if (phase !== "confirmKeep" && phase !== "confirmDrop") return;
    const doc = (globalThis as { document?: Document }).document;
    if (!doc?.addEventListener) return;
    const onKey = (event: { key?: string; preventDefault?: () => void }) => {
      if (event.key !== "Escape") return;
      event.preventDefault?.();
      cancelDialog();
    };
    doc.addEventListener("keydown", onKey);
    return () => doc.removeEventListener("keydown", onKey);
  }, [phase, cancelDialog]);

  const hardwareBackRef = useRef(hardwareBack);
  hardwareBackRef.current = hardwareBack;

  useEffect(() => {
    if (Platform.OS !== "web" || !active || !storageReady) return;
    const hist = globalThis.history;
    const win = globalThis.window;
    if (!hist?.pushState || !win?.addEventListener) return;
    hist.pushState({ finderGate: true }, "");
    const onPop = () => {
      hardwareBackRef.current();
      hist.pushState({ finderGate: true }, "");
    };
    win.addEventListener("popstate", onPop);
    return () => win.removeEventListener("popstate", onPop);
  }, [active, storageReady]);

  useEffect(() => {
    if (!active) return;
    try {
      if (phase === "editing" && nameHint) {
        nameRef.current?.focus?.();
        return;
      }
      if (phase === "confirmKeep" || phase === "confirmDrop") {
        focusNode(dialogTitleRef.current);
        return;
      }
      if (phase === "editing") focusNode(titleRef.current);
      if (phase === "handover") focusNode(handoverTitleRef.current);
    } catch (error) {
      setRenderError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [active, phase, nameHint]);

  useEffect(() => {
    if (Platform.OS !== "web" || !active) return;
    const root = gateRef.current as unknown as HTMLElement | null;
    return bindWebOverlayTrap(root, () => {
      try {
        if (phase === "confirmKeep" || phase === "confirmDrop") {
          focusNode(dialogTitleRef.current);
          return;
        }
        if (phase === "handover") {
          focusNode(handoverTitleRef.current);
          return;
        }
        if (nameHint) {
          nameRef.current?.focus?.();
          return;
        }
        focusNode(titleRef.current);
      } catch (error) {
        setRenderError(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }, [active, phase, nameHint]);

  if (renderError) throw renderError;

  const shellStyle = {
    pointerEvents: "auto" as const,
    position: Platform.OS === "web" ? ("fixed" as "absolute") : "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 10000,
    elevation: 10000,
    backgroundColor: theme.color.bg,
    paddingTop: insets.top,
    paddingBottom: insets.bottom,
  } as const;

  if (!storageReady) {
    return (
      <View
        style={shellStyle}
        accessibilityViewIsModal
        accessibilityLabel="Finder-Sperre"
      >
        <LinearGradient
          colors={getScreenGradientColors(theme)}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
        />
      </View>
    );
  }

  if (!active || phase == null) return null;

  const webDialog =
    Platform.OS === "web"
      ? {
          role: "dialog" as const,
          "aria-modal": true,
        }
      : {};

  const dialog =
    phase === "confirmKeep"
      ? {
          title: FINDER_KEEP_DIALOG_TITLE,
          body: FINDER_KEEP_DIALOG_BODY,
          primary: FINDER_KEEP_CONFIRM,
          onPrimary: confirmKeep,
          secondary: FINDER_CONTINUE,
          onSecondary: cancelDialog,
        }
      : phase === "confirmDrop"
        ? {
            title: FINDER_DROP_DIALOG_TITLE,
            body: FINDER_DROP_DIALOG_BODY,
            primary: FINDER_CONTINUE,
            onPrimary: cancelDialog,
            secondary: FINDER_DROP_CONFIRM,
            onSecondary: confirmDiscard,
          }
        : null;

  return (
    <View
      ref={gateRef}
      style={shellStyle}
      accessibilityViewIsModal
      accessibilityLabel="Finder-Angaben"
      {...webDialog}
    >
      <LinearGradient
        colors={getScreenGradientColors(theme)}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
      />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "web" ? undefined : "padding"}
      >
        <ScrollView
          contentContainerStyle={{
            padding: theme.space.lg,
            paddingBottom: theme.space.xxl,
            gap: theme.space.md,
            maxWidth: theme.layout.readable,
            width: "100%",
            alignSelf: "center",
          }}
          keyboardShouldPersistTaps="handled"
        >
          {phase === "handover" ? (
            <Card>
              <View style={{ gap: theme.space.md }}>
                <Text
                  ref={handoverTitleRef}
                  accessibilityRole="header"
                  accessible
                  {...webTitleProps}
                  style={{
                    color: theme.color.text,
                    fontSize: theme.fontSize.title,
                    fontWeight: "700",
                  }}
                >
                  {FINDER_HANDOVER_TITLE}
                </Text>
                <Text
                  style={{
                    color: theme.color.text,
                    fontSize: theme.fontSize.body,
                    lineHeight: 22,
                  }}
                >
                  {FINDER_HANDOVER_BODY}
                </Text>
                <AppButton style={actionStyle} onPress={completeHandover}>
                  {FINDER_HANDOVER_ACTION}
                </AppButton>
              </View>
            </Card>
          ) : (
            <>
              <View
                style={{ alignItems: "center" }}
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
                aria-hidden={true}
              >
                <LogoFerinaCura
                  width={156}
                  height={56}
                  fill={theme.color.primary}
                />
              </View>
              <Card>
                <View style={{ gap: theme.space.md }}>
                  <Text
                    ref={titleRef}
                    accessibilityRole="header"
                    accessible
                    {...webTitleProps}
                    style={{
                      color: theme.color.text,
                      fontSize: theme.fontSize.title,
                      fontWeight: "700",
                    }}
                  >
                    {FINDER_GATE_TITLE}
                  </Text>
                  <Text
                    style={{
                      color: theme.color.text,
                      fontSize: theme.fontSize.body,
                      lineHeight: 22,
                    }}
                  >
                    {FINDER_GATE_INTRO}
                  </Text>
                  <FinderPrivacyNotice version={draft.privacyNoticeVersion} />
                  <FinderPrivacyAck
                    checked={draft.privacyDocumented}
                    onChange={(privacyDocumented) => updateDraft({ privacyDocumented })}
                  />
                  <FinderDraftFields
                    draft={draft}
                    onChange={updateDraft}
                    nameHint={nameHint ? FINDER_NAME_HINT : null}
                    nameInputRef={nameRef}
                  />
                  <View style={{ gap: theme.space.sm }}>
                    <AppButton style={actionStyle} onPress={requestFinish}>
                      {FINDER_FINISH_LABEL}
                    </AppButton>
                    <AppButton
                      variant="ghost"
                      style={actionStyle}
                      onPress={requestDiscard}
                    >
                      {FINDER_DISCARD_LABEL}
                    </AppButton>
                  </View>
                </View>
              </Card>
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>

      {dialog ? (
        <View
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: theme.color.overlay,
            justifyContent: "center",
            padding: theme.space.lg,
          }}
          accessibilityViewIsModal
          accessibilityLabel={dialog.title}
        >
          <Pressable
            onPress={cancelDialog}
            accessible={false}
            importantForAccessibility="no"
            style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
          />
          <View
            style={{
              backgroundColor: theme.color.panelContentBg,
              borderRadius: theme.radius.lg,
              padding: theme.space.md,
              gap: theme.space.sm,
              maxWidth: theme.layout.readable,
              width: "100%",
              alignSelf: "center",
              borderWidth: 1,
              borderColor: theme.color.border,
            }}
          >
            <Text
              ref={dialogTitleRef}
              accessibilityRole="header"
              {...webTitleProps}
              style={{
                color: theme.color.text,
                fontWeight: "700",
                fontSize: theme.fontSize.h3,
              }}
            >
              {dialog.title}
            </Text>
            <Text
              style={{
                color: theme.color.textSecondary,
                fontSize: theme.fontSize.body,
                lineHeight: 22,
              }}
            >
              {dialog.body}
            </Text>
            <AppButton style={actionStyle} onPress={dialog.onPrimary}>
              {dialog.primary}
            </AppButton>
            <AppButton
              variant="outline"
              style={actionStyle}
              onPress={dialog.onSecondary}
            >
              {dialog.secondary}
            </AppButton>
          </View>
        </View>
      ) : null}
    </View>
  );
}
